import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreConfigService } from '../config/core-config.service';
import { sortIndexAscend, ObjectUtils } from '@ark-libs/ngx-utils'

@Injectable({ providedIn:'root' })
export class DataDictService {

    // private data: { [key: string]: { text?: string, value?: any, seq?: number }[] } = null;
    private data: { [key: string]: any[] } = {};
    private dataCache: { [key: string]: any[] } = {};
    private dataPromise: { [key: string]: Promise<any[]> } = {};

    private dictTextFormatters: { [key: string]: any } = {};
    dictDataLoaded: EventEmitter<{ kind: string, items: any[] }> = new EventEmitter();

    constructor(
        private http: HttpClient,
        private configService: CoreConfigService,
    ) { }

    private get apiUrl(): string {
        return this.configService.apiUrls.dictMap;
    }

    private loadDictData(dictKind: string): Observable<any[]> {
        let result = this.dataCache[dictKind];
        if(result) return of(result);

        let promise = this.dataPromise[dictKind];
        if(!promise){
            const params = { kind: dictKind, size: '9999' };
            promise = this.http.get<any[]>(this.apiUrl, { params: params }).pipe(map(dictItems => {
                if (!dictItems) { dictItems = []; } else { dictItems = dictItems.sort(sortIndexAscend); }
                this.parseCascader(dictItems);
                this.putItems(dictKind, dictItems);
                this.dictDataLoaded.emit({ kind: dictKind, items: dictItems });
                this.dataCache[dictKind] = dictItems;
                delete this.dataPromise[dictKind];
                return dictItems;
            })).toPromise<any[]>();
            this.dataPromise[dictKind] = promise;
        }
        return from(promise);
    }
    private isValueEqual(dictItem: any, value: any): boolean {
        const v1 = dictItem.value ? dictItem.value.toString() : '';
        const v2 = value ? value.toString() : '';
        return v1 === v2;
    }

    getPresentItems(dictKind: string, cascader?: boolean): any[] {
        const dictItems = this.data[dictKind];
        if(dictItems == null) return [];

        return cascader?this.toCascader(dictItems):dictItems;
    }
    toCascader(dictItems:any[]){
        return this.parseCascader(dictItems);
    }
    private parseCascader(dictItems:any[]){
        dictItems.forEach(item => {
            if(item.___cascader) return; item.___cascader = true;

            const parentId = item.parentId;
            if(parentId == null) return;
            dictItems.filter(p=>p.id==parentId).map(parent=>{
                item.parent = parent;
                if(parent.children) parent.children.push(item);
                else parent.children = [item];
            });
        });
        dictItems.filter(item=>item.children==null).forEach(item=>item.isLeaf=true);
        return dictItems.filter(item=>item.parentId==null);
    }
    putItems(dictKind: string, dictItems: any[], textFormatter?: any): void {
        this.data[dictKind] = dictItems;
        if (textFormatter) this.dictTextFormatters[dictKind] = textFormatter;
    }
    getItems(dictKind: string, cascader?: boolean): Observable<any[]> {
        const dictItems = this.data[dictKind];
        if (dictItems != null) { return of(cascader?this.toCascader(dictItems):dictItems); }
        return this.loadDictData(dictKind).pipe(map(items=>cascader?this.toCascader(items):items));
    }
    getItem(dictKind: string, value: any): Observable<any> {
        const dictItems = this.data[dictKind];
        if (dictItems != null) {
            return of(
                dictItems.filter(item => this.isValueEqual(item, value)).pop()
            );
        }
        return this.getItems(dictKind).pipe(map(dictItems => {
            return dictItems.filter(item => this.isValueEqual(item, value)).pop();
        }));
    }

    getItemText(dictKind: string, value: any, format?: string): Observable<string> {
        if (dictKind == null) return of(value);
        return this.getItem(dictKind, value).pipe(map(item => {
            if (item) return this.parseItemText(dictKind, item, format);
            else return value;
        }));
    }
    parseItemText(dictKind: string, item: any, format?: string): string {
        if (item == null) return "";
        const formatter = this.dictTextFormatters[dictKind];
        if (formatter instanceof Function) return formatter(item);

        if (format == null && typeof formatter === 'string') format = formatter;
        if (format == null || format.indexOf('${') == -1) { return item.text; }
        let text = format;
        Object.keys(item).forEach(p => {
            text = text.replace('${' + p + '}', item[p]);
        })
        return text;
    }

    getDictItemMuiltiLevelPath(kind, value, returnField?:'value'|'text'|string) {
        var dictItems = this.getPresentItems(kind);
        if (dictItems == null) { return []; }

        let item = dictItems.filter(x=>x.value == value).pop();
        const path = [];
        while (item) {
            path.push(returnField?item[returnField]:item);
            item = item.parent;
            if(item==null||path.filter(x=>x==item).length>0) break;
        }
        return path.reverse();
    }
    getLastLevelValueInPath(path:any[]){
        if(path==null||path.length==0) return null;
        const value = path[path.length-1];
        if(value instanceof Object) return value['value'];
        return value;
    }

    onDictLoaded(kinds:string[], callback: Function){
        if(kinds.length==0||callback == null) return;
        const loadedKinds = [];
        kinds.forEach(kind=>{
            if(this.data[kind]!=null) loadedKinds.push(kind);
        });
        if(loadedKinds.length == kinds.length) {callback();return;}

        const subscription = this.dictDataLoaded.subscribe(event=>{
            const kind = event.kind;
            if(kinds.includes(kind)){
                if(!loadedKinds.includes(kind))loadedKinds.push(kind);
                if(loadedKinds.length == kinds.length){
                    subscription.unsubscribe();
                    callback();
                }
            }
        });
    }
}
